import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { learningService } from '../services/learningService';
import { BilingualTitle } from '../components/common/BilingualTitle';
import { Note } from '../types/learning';
import NoteEditor from '../components/learning/NoteEditor';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import './NotesPage.css';

// SVG Icons (Lucide-style)
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <path d="m21 21-4.3-4.3"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14M5 12h14"/>
  </svg>
);

const StarIcon = ({ filled = false }: { filled?: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const PinIcon = ({ filled = false }: { filled?: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 17v5"/>
    <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4.76Z"/>
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
    <path d="m15 5 4 4"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18"/>
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
  </svg>
);

const BookIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const FileTextIcon = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/>
    <path d="M14 2v4a2 2 0 0 0 2 2h4"/>
    <path d="M10 9H8"/>
    <path d="M16 13H8"/>
    <path d="M16 17H8"/>
  </svg>
);

// Tag type icons
const TagIcons: Record<string, React.ReactNode> = {
  vocabulary: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
    </svg>
  ),
  grammar: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  pronunciation: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
      <line x1="12" y1="19" x2="12" y2="22"/>
    </svg>
  ),
  'ai-generated': (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 8V4H8"/>
      <rect width="16" height="12" x="4" y="8" rx="2"/>
      <path d="M2 14h2"/>
      <path d="M20 14h2"/>
      <path d="M15 13v2"/>
      <path d="M9 13v2"/>
    </svg>
  ),
};

// Helper function to determine note type from tags
const getNoteType = (tags: string[]): string => {
  if (tags.includes('ai-generated')) return 'ai';
  if (tags.includes('vocabulary')) return 'vocabulary';
  if (tags.includes('grammar')) return 'grammar';
  if (tags.includes('pronunciation')) return 'pronunciation';
  return 'general';
};

// Group notes by date
const groupNotesByDate = (notes: Note[], tCommon: (key: string) => string): Map<string, Note[]> => {
  const groups = new Map<string, Note[]>();
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  notes.forEach(note => {
    const noteDate = new Date(note.updatedAt);
    let dateKey: string;

    if (noteDate.toDateString() === today.toDateString()) {
      dateKey = tCommon('today');
    } else if (noteDate.toDateString() === yesterday.toDateString()) {
      dateKey = tCommon('yesterday');
    } else {
      dateKey = noteDate.toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: noteDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
      });
    }

    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)?.push(note);
  });

  return groups;
};

const NotesPage: React.FC = () => {
  const { t } = useTranslation('learning');
  const { t: tCommon } = useTranslation('common');
  const [notes, setNotes] = useState<Note[]>([]);
  const [filteredNotes, setFilteredNotes] = useState<Note[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const [filterStarred, setFilterStarred] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<Note | null>(null);

  const getNoteTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      vocabulary: t('notesPage.typeVocabulary'),
      grammar: t('notesPage.typeGrammar'),
      pronunciation: t('notesPage.typePronunciation'),
      ai: t('notesPage.typeAI'),
      general: t('notesPage.typeNote'),
    };
    return labels[type] || t('notesPage.typeNote');
  };

  const getFilterTabLabel = (tag: string): string => {
    const labels: Record<string, string> = {
      vocabulary: t('notesPage.typeVocabulary'),
      grammar: t('notesPage.typeGrammar'),
      pronunciation: t('notesPage.typePronunciation'),
      'ai-generated': t('notesPage.typeAI'),
    };
    return labels[tag] || tag.charAt(0).toUpperCase() + tag.slice(1);
  };

  const loadNotes = async () => {
    setLoading(true);
    try {
      const fetchedNotes = await learningService.getAllNotes();
      setNotes(fetchedNotes);

      const tags = new Set<string>();
      fetchedNotes.forEach(note => {
        note.tags.forEach(tag => tags.add(tag));
      });
      setAllTags(Array.from(tags));
    } catch (error) {
      console.error('Failed to load notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterNotes = useCallback(() => {
    let filtered = [...notes];

    if (searchQuery) {
      filtered = filtered.filter(note =>
        note.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        note.relatedWord?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        note.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (filterTag) {
      filtered = filtered.filter(note => note.tags.includes(filterTag));
    }

    if (filterStarred) {
      filtered = filtered.filter(note => note.isPinned);
    }

    filtered.sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );

    setFilteredNotes(filtered);
  }, [notes, searchQuery, filterTag, filterStarred]);

  useEffect(() => {
    loadNotes();
  }, []);

  useEffect(() => {
    filterNotes();
  }, [filterNotes]);

  const groupedNotes = useMemo(() => groupNotesByDate(filteredNotes, tCommon), [filteredNotes, tCommon]);

  const handleCreateNote = () => {
    setSelectedNote(null);
    setShowEditor(true);
  };

  const handleEditNote = (note: Note, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedNote(note);
    setShowEditor(true);
  };

  const handleDeleteNoteClick = (note: Note, e: React.MouseEvent) => {
    e.stopPropagation();
    setNoteToDelete(note);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!noteToDelete) return;

    try {
      await learningService.deleteNote(noteToDelete.id);
      setNotes(notes.filter(n => n.id !== noteToDelete.id));
    } catch (error) {
      console.error('Failed to delete note:', error);
    } finally {
      setDeleteModalOpen(false);
      setNoteToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setNoteToDelete(null);
  };

  const handleTogglePin = async (note: Note, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await learningService.togglePin(note.id);
      setNotes(notes.map(n =>
        n.id === note.id ? { ...n, isPinned: !n.isPinned } : n
      ));
    } catch (error) {
      console.error('Failed to toggle pin:', error);
    }
  };

  const handleSaveNote = async (noteData: Partial<Note>) => {
    try {
      if (selectedNote) {
        const updated = await learningService.updateNote(selectedNote.id, {
          content: noteData.content,
          tags: noteData.tags,
          relatedWord: noteData.relatedWord ?? undefined
        });
        setNotes(notes.map(n => n.id === updated.id ? updated : n));
      } else {
        const newNote = await learningService.createNote({
          courseId: 'course1',
          content: noteData.content || '',
          tags: noteData.tags || [],
          relatedWord: noteData.relatedWord ?? undefined
        });
        setNotes([newNote, ...notes]);
      }
      setShowEditor(false);
    } catch (error) {
      console.error('Failed to save note:', error);
    }
  };

  const formatTimestamp = (timestamp: number | null): string => {
    if (!timestamp) return '';
    const minutes = Math.floor(timestamp / 60000);
    const seconds = Math.floor((timestamp % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Count notes by category
  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    notes.forEach(note => {
      note.tags.forEach(tag => {
        counts[tag] = (counts[tag] || 0) + 1;
      });
    });
    return counts;
  }, [notes]);

  const pinnedCount = useMemo(() => notes.filter(n => n.isPinned).length, [notes]);

  if (loading) {
    return (
      <div className="notes-page">
        <div className="notes-container">
          <div className="notes-loading">
            <div className="notes-loading-spinner" />
            <span className="notes-loading-text">{t('notesPage.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="notes-page">
      <div className="notes-container">
        {/* Header */}
        <div className="notes-header">
          <div className="notes-title">
            <h1>{t('notesPage.title')}</h1>
            <span className="notes-count">{notes.length}</span>
          </div>
          <div className="notes-header-actions">
            <div className="notes-search">
              <span className="notes-search-icon"><SearchIcon /></span>
              <input
                type="text"
                placeholder={t('notesPage.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="notes-search-input"
              />
            </div>
            <button className="create-note-btn" onClick={handleCreateNote}>
              <PlusIcon />
              {t('notesPage.newNote')}
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="notes-filters">
          <button
            className={`filter-tab ${!filterTag && !filterStarred ? 'filter-tab--active' : ''}`}
            onClick={() => { setFilterTag(null); setFilterStarred(false); }}
          >
            {t('notesPage.filterAll')}
            <span className="filter-tab-count">{notes.length}</span>
          </button>

          {pinnedCount > 0 && (
            <button
              className={`filter-tab filter-tab--starred ${filterStarred ? 'filter-tab--active' : ''}`}
              onClick={() => { setFilterStarred(!filterStarred); setFilterTag(null); }}
            >
              <StarIcon filled={filterStarred} />
              {t('notesPage.filterStarred')}
              <span className="filter-tab-count">{pinnedCount}</span>
            </button>
          )}

          {['vocabulary', 'grammar', 'pronunciation'].map(tag => {
            if (!tagCounts[tag]) return null;
            return (
              <button
                key={tag}
                className={`filter-tab ${filterTag === tag ? 'filter-tab--active' : ''}`}
                onClick={() => { setFilterTag(filterTag === tag ? null : tag); setFilterStarred(false); }}
              >
                {TagIcons[tag]}
                {getFilterTabLabel(tag)}
                <span className="filter-tab-count">{tagCounts[tag]}</span>
              </button>
            );
          })}
        </div>

        {/* Notes Timeline */}
        {filteredNotes.length === 0 ? (
          <div className="notes-empty">
            <FileTextIcon />
            <h3 className="notes-empty-title">
              {searchQuery || filterTag || filterStarred
                ? t('notesPage.emptyNoResults')
                : t('notesPage.emptyNoNotes')}
            </h3>
            <p className="notes-empty-desc">
              {searchQuery || filterTag || filterStarred
                ? t('notesPage.emptyNoResultsDesc')
                : t('notesPage.emptyNoNotesDesc')}
            </p>
          </div>
        ) : (
          <div className="notes-timeline">
            {Array.from(groupedNotes.entries()).map(([dateKey, dateNotes]) => (
              <div key={dateKey} className="notes-date-group">
                <div className="notes-date-header">{dateKey}</div>
                <div className="notes-list">
                  {dateNotes.map(note => {
                    const noteType = getNoteType(note.tags);
                    return (
                      <div
                        key={note.id}
                        className={`note-card ${note.isPinned ? 'note-card--pinned' : ''}`}
                        onClick={() => { setSelectedNote(note); setShowEditor(true); }}
                      >
                        {/* Type Header */}
                        <div className="note-card__type">
                          <span className={`note-type-badge note-type-badge--${noteType}`}>
                            {TagIcons[noteType === 'ai' ? 'ai-generated' : noteType] || TagIcons.vocabulary}
                            {getNoteTypeLabel(noteType)}
                          </span>
                          <div className="note-card__actions">
                            <button
                              className={`note-action-btn note-action-btn--pin ${note.isPinned ? 'note-action-btn--active' : ''}`}
                              onClick={(e) => handleTogglePin(note, e)}
                              title={note.isPinned ? t('notesPage.unpin') : t('notesPage.pin')}
                            >
                              <PinIcon filled={note.isPinned} />
                            </button>
                            <button
                              className="note-action-btn"
                              onClick={(e) => handleEditNote(note, e)}
                              title={tCommon('edit')}
                            >
                              <EditIcon />
                            </button>
                            <button
                              className="note-action-btn note-action-btn--delete"
                              onClick={(e) => handleDeleteNoteClick(note, e)}
                              title={tCommon('delete')}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="note-card__content">
                          {note.relatedWord && (
                            <h4 className="note-card__title">{note.relatedWord}</h4>
                          )}
                          <p className="note-card__body">{note.content}</p>
                        </div>

                        {/* Tags */}
                        {note.tags.length > 0 && (
                          <div className="note-card__tags">
                            {note.tags.filter(tag => !['vocabulary', 'grammar', 'pronunciation', 'ai-generated'].includes(tag)).map(tag => (
                              <span key={tag} className="note-tag">#{tag}</span>
                            ))}
                          </div>
                        )}

                        {/* Footer */}
                        <div className="note-card__footer">
                          {note.courseId && (
                            <Link
                              to={(() => {
                                const hasS = note.sentenceIndex !== null && note.sentenceIndex !== undefined;
                                if (!hasS) return `/courses/${note.courseId}`;
                                const params = new URLSearchParams();
                                if (note.episodeId) params.set('eid', note.episodeId);
                                params.set('s', String(note.sentenceIndex));
                                return `/courses/${note.courseId}?${params.toString()}`;
                              })()}
                              className="note-course-link"
                              onClick={(e) => e.stopPropagation()}
                              title={note.courseTitle || t('notesPage.openCourse')}
                            >
                              <BookIcon />
                              {note.courseTitle ? (
                                <BilingualTitle
                                  title={note.courseTitle}
                                  titles={note.courseTitles}
                                  variant="compact"
                                  as="span"
                                  className="note-course-link__text"
                                />
                              ) : (
                                <span className="note-course-link__text">
                                  {t('notesPage.openCourse')}
                                </span>
                              )}
                            </Link>
                          )}
                          {note.timestamp && (
                            <span className="note-timestamp">
                              <ClockIcon />
                              {formatTimestamp(note.timestamp)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Note Editor Modal */}
        {showEditor && (
          <NoteEditor
            note={selectedNote}
            onSave={handleSaveNote}
            onClose={() => setShowEditor(false)}
            availableTags={allTags}
          />
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={deleteModalOpen}
          title={t('notesPage.deleteTitle')}
          message={t('notesPage.deleteMessage')}
          itemName={noteToDelete?.content.substring(0, 50) + (noteToDelete?.content && noteToDelete.content.length > 50 ? '...' : '')}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </div>
    </div>
  );
};

export default NotesPage;
